/**
 * Custom hook for parameter management in Data Adjustments
 * Handles all parameter state and validation
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { 
  DateTimeState,
  ParameterValidationErrors,
  FileMethod
} from '../types/dataAdjustments.types';
import { 
  DEFAULT_PARAMETERS,
  validateNumericField 
} from '../types/dataAdjustments.types';

interface UseAdjustmentsParametersReturn {
  // Parameter state
  startTime: DateTimeState;
  endTime: DateTimeState;
  timeStepSize: string;
  offset: string;
  anchorTime: string;
  isConst: boolean;
  selectedMethods: { [key: string]: string };
  intrplMaxValues: { [key: string]: string };
  decimalPlaces: { [key: string]: string };
  methodsRequired: boolean;
  filesNeedingMethods: FileMethod[];

  // Validation
  validationErrors: ParameterValidationErrors;

  // Setters
  setStartTime: (value: string, timestamp: number | null) => void;
  setEndTime: (value: string, timestamp: number | null) => void;
  setTimeStepSize: (value: string) => void;
  setOffset: (value: string) => void;
  setAnchorTime: (value: string) => void;
  setSelectedMethod: (filename: string, method: string) => void;
  setIntrplMaxValue: (filename: string, value: string) => void;
  setDecimalPlaces: (filename: string, value: string) => void;
  setMethodsRequired: (required: boolean) => void;
  setFilesNeedingMethods: (files: FileMethod[]) => void;

  // Helpers
  validateParameters: () => boolean;
  allMethodsSelected: () => boolean;
  resetParameters: () => void;
}

export const useAdjustmentsParameters = (): UseAdjustmentsParametersReturn => {
  const { t } = useTranslation('dataAdjustments');
  
  // Parameter states
  const [startTime, setStartTimeState] = useState<DateTimeState>(DEFAULT_PARAMETERS.startTime);
  const [endTime, setEndTimeState] = useState<DateTimeState>(DEFAULT_PARAMETERS.endTime);
  const [timeStepSize, setTimeStepSizeState] = useState<string>(DEFAULT_PARAMETERS.timeStepSize);
  const [offset, setOffsetState] = useState<string>(DEFAULT_PARAMETERS.offset);
  const [selectedMethods, setSelectedMethodsState] = useState<{ [key: string]: string }>(DEFAULT_PARAMETERS.selectedMethods);
  const [intrplMaxValues, setIntrplMaxValuesState] = useState<{ [key: string]: string }>(DEFAULT_PARAMETERS.intrplMaxValues);
  const [decimalPlaces, setDecimalPlacesState] = useState<{ [key: string]: string }>(DEFAULT_PARAMETERS.decimalPlaces);
  const [methodsRequired, setMethodsRequiredState] = useState<boolean>(false);
  const [filesNeedingMethods, setFilesNeedingMethodsState] = useState<FileMethod[]>([]);
  const [anchorTime, setAnchorTimeState] = useState<string>('');

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<ParameterValidationErrors>({});

  // Derived: whether TSS is an exact divisor of 60 (const grid → offset input)
  // defaults to true (offset input) when TSS is empty or invalid
  const tssNum = parseFloat(timeStepSize);
  const isConst = !Number.isFinite(tssNum) || tssNum <= 0
    || Math.abs((60 / tssNum) - Math.round(60 / tssNum)) < 1e-9;

  /**
   * Set start time with validation
   */
  const setStartTime = useCallback((value: string, timestamp: number | null) => {
    setStartTimeState({ value, timestamp });
    
    // Clear validation error
    setValidationErrors(prev => ({ ...prev, startTime: undefined }));
  }, []);

  /**
   * Set end time with validation
   */
  const setEndTime = useCallback((value: string, timestamp: number | null) => {
    setEndTimeState({ value, timestamp });
    
    // Clear validation error
    setValidationErrors(prev => ({ ...prev, endTime: undefined }));
  }, []);

  /**
   * Set time step size with validation
   */
  const setTimeStepSize = useCallback((value: string) => {
    setTimeStepSizeState(value);
    
    // Validate immediately
    const validation = validateNumericField(value, 'timeStepSize', t);
    setValidationErrors(prev => ({
      ...prev,
      timeStepSize: validation.isValid ? undefined : validation.errorMessage
    }));
  }, [t]);

  /**
   * Set offset with validation
   */
  const setOffset = useCallback((value: string) => {
    setOffsetState(value);
    
    // Validate immediately
    const validation = validateNumericField(value, 'offset', t);
    setValidationErrors(prev => ({
      ...prev,
      offset: validation.isValid ? undefined : validation.errorMessage
    }));
  }, [t]);

  /**
   * Set selected method for a file
   */
  const setSelectedMethod = useCallback((filename: string, method: string) => {
    setSelectedMethodsState(prev => ({
      ...prev,
      [filename]: method
    }));

    // Clear intrpl_max if method doesn't require it
    if (method !== 'intrpl' && method !== 'nearest (max. delta)') {
      setIntrplMaxValuesState(prev => {
        const newValues = { ...prev };
        delete newValues[filename];
        return newValues;
      });
    }
  }, []);

  /**
   * Set intrpl max value for a file
   */
  const setIntrplMaxValue = useCallback((filename: string, value: string) => {
    setIntrplMaxValuesState(prev => ({
      ...prev,
      [filename]: value
    }));
  }, []);

  /**
   * Set decimal places for a file
   */
  const setDecimalPlaces = useCallback((filename: string, value: string) => {
    setDecimalPlacesState(prev => ({
      ...prev,
      [filename]: value
    }));
  }, []);

  /**
   * Set anchor time
   */
  const setAnchorTime = useCallback((value: string) => {
    setAnchorTimeState(value);
  }, []);

  /**
   * Set methods required flag
   */
  const setMethodsRequired = useCallback((required: boolean) => {
    setMethodsRequiredState(required);
  }, []);

  /**
   * Set files needing methods
   */
  const setFilesNeedingMethods = useCallback((files: FileMethod[]) => {
    setFilesNeedingMethodsState(files);
  }, []);

  /**
   * Validate all parameters
   */
  const validateParameters = useCallback((): boolean => {
    const errors: ParameterValidationErrors = {};
    
    // Validate start time
    if (!startTime.value.trim()) {
      errors.startTime = t('validation.required', { field: 'Start Time' });
    }
    
    // Validate end time
    if (!endTime.value.trim()) {
      errors.endTime = t('validation.required', { field: 'End Time' });
    }
    
    // Validate time step size
    const tssValidation = validateNumericField(timeStepSize, 'timeStepSize', t);
    if (!tssValidation.isValid) {
      errors.timeStepSize = tssValidation.errorMessage;
    }
    
    // Validate offset
    const offsetValidation = validateNumericField(offset, 'offset', t);
    if (!offsetValidation.isValid) {
      errors.offset = offsetValidation.errorMessage;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [startTime, endTime, timeStepSize, offset, t]);

  /**
   * Check if all methods are selected
   */
  const allMethodsSelected = useCallback((): boolean => {
    return filesNeedingMethods.every((file) => {
      const method = selectedMethods[file.filename];
      if (!method) return false;

      // Check if intrpl_max is required and provided
      if (method === 'intrpl' || method === 'nearest (max. delta)') {
        const intrplMax = intrplMaxValues[file.filename];
        return intrplMax && intrplMax.trim() !== '' && Number(intrplMax) > 0;
      }

      return true;
    });
  }, [filesNeedingMethods, selectedMethods, intrplMaxValues]);

  /**
   * Reset all parameters
   */
  const resetParameters = useCallback(() => {
    setStartTimeState(DEFAULT_PARAMETERS.startTime);
    setEndTimeState(DEFAULT_PARAMETERS.endTime);
    setTimeStepSizeState(DEFAULT_PARAMETERS.timeStepSize);
    setOffsetState(DEFAULT_PARAMETERS.offset);
    setSelectedMethodsState(DEFAULT_PARAMETERS.selectedMethods);
    setIntrplMaxValuesState(DEFAULT_PARAMETERS.intrplMaxValues);
    setDecimalPlacesState(DEFAULT_PARAMETERS.decimalPlaces);
    setMethodsRequiredState(false);
    setFilesNeedingMethodsState([]);
    setAnchorTimeState('');
    setValidationErrors({});
  }, []);

  return {
    // State
    startTime,
    endTime,
    timeStepSize,
    offset,
    anchorTime,
    isConst,
    selectedMethods,
    intrplMaxValues,
    decimalPlaces,
    methodsRequired,
    filesNeedingMethods,

    // Validation
    validationErrors,

    // Setters
    setStartTime,
    setEndTime,
    setTimeStepSize,
    setOffset,
    setAnchorTime,
    setSelectedMethod,
    setIntrplMaxValue,
    setDecimalPlaces,
    setMethodsRequired,
    setFilesNeedingMethods,

    // Helpers
    validateParameters,
    allMethodsSelected,
    resetParameters
  };
};
